import { getLinkAttributes, stripSiteDomain } from "./url.js";

/**
 * Escapes HTML special characters to prevent XSS
 */
function escapeHtml(str: string): string {
    return str
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

/**
 * Validates that a URL uses an allowed protocol
 */
function isValidHref(href: string): boolean {
    const trimmed = href.trim();
    // Allow relative URLs starting with /
    if (trimmed.startsWith("/")) return true;
    // Allow http and https
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return true;
    // Allow mailto and tel
    if (trimmed.startsWith("mailto:") || trimmed.startsWith("tel:")) return true;
    return false;
}

/**
 * Converts markdown-style links [text](url) and **bold** to HTML,
 * and preserves newlines as <br> tags.
 * The entire input is HTML-escaped first to prevent XSS.
 * Example: "Visit [our site](/about)" -> "Visit <a href=\"/about\">our site</a>"
 */
export function parseLinks(text: string): string {
    if (!text) return "";
    return (
        escapeHtml(text)
            // **bold** → <strong>bold</strong>
            .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
            // [text](url) → <a href="url">text</a>
            .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, linkText, href) => {
                if (isValidHref(href)) {
                    const linkHref = stripSiteDomain(href);
                    const linkAttributes = getLinkAttributes(linkHref);
                    const attributes = Object.entries(linkAttributes)
                        .map(([name, value]) => ` ${name}="${value}"`)
                        .join("");

                    return `<a href="${linkHref}"${attributes}>${linkText}</a>`;
                }
                return linkText;
            })
            .replace(/\n/g, "<br>")
    );
}
